
import { Inject } from '@odinjs/odin';
import { BasicFile, FileUploaderAPI, FormAPI, MapperAPI, SegmentedControlAPI } from '@philips-internal/oswald-ui-components';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeSubmit, OnChange, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { AbortableSubmitEvent, ChangeEvent, ClickEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment/moment.js';
import { FragmentNames, PseudonymSuggestionCheckAvailableAliases } from '../../../codegen';
import { ScheduleItem, SuggestionPeriodItem } from '../../../interfaces/schedules-suggestions';
import PseudonymAppointmentsBaseController from '../pseudonym-appointments-base-controller';
import { SchedulePeriodType } from '../pseudonym-types';
import { FileRuleUtils } from '../../../utils/file-rules-utils';

interface SuggestionPeriodForm {
  observation: string;
  period: Array<SuggestionPeriodItem>;
  uploadFiles?: Array<BasicFile>;
}

const STATIC_SCHEDULE_PERIOD_DATASOURCE = [
  {
    id: 1,
    value: 'patient.morning'
  },
  {
    id: 2,
    value: 'patient.afternoon'
  },
  {
    id: 3,
    value: 'patient.night'
  }
];

@Controller({ component: PseudonymSuggestionCheckAvailableAliases.LAYOUT_SUGGESTION_CHECK_AVAILABLE, fragment: FragmentNames.PSEUDONYM_SUGGESTION_CHECK_AVAILABLE })
export default class SuggestionRequestModalController extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymSuggestionCheckAvailableAliases.FORM_REQUEST)
    formRequestApi!: FormAPI;

  @Inject(PseudonymSuggestionCheckAvailableAliases.MAPPER_AVATAR)
    mapperAvatarApi!: MapperAPI;

  @Inject(PseudonymSuggestionCheckAvailableAliases.SEGMENTED_CONTROL_PERIOD)
    segmentedControlPeriodApi!: SegmentedControlAPI;

  @Inject(PseudonymSuggestionCheckAvailableAliases.FILE_UPLOADER)
    fileUploderApi!: FileUploaderAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @OnMount({ target: PseudonymSuggestionCheckAvailableAliases.LAYOUT_SUGGESTION_CHECK_AVAILABLE_CONTENT })
  handleOnMountLayoutSuggestionCheckAvailableContent(event: MountEvent): void {
    if (!event) {
      return
    }

    const suggestionItem = this.context.get('data') as ScheduleItem;
    this.mapperAvatarApi.setData(suggestionItem as unknown as Record<string, Value>);
    this.formRequestApi.setValue({ selectedDate: moment(suggestionItem.date, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT) });
    this.segmentedControlPeriodApi.setData(STATIC_SCHEDULE_PERIOD_DATASOURCE);
  }

  @OnMount(PseudonymSuggestionCheckAvailableAliases.FILE_UPLOADER)
  handleOnMountFileUploader(event: MountEvent): void {
    const fileUploaderApi = event.target as FileUploaderAPI;
    FileRuleUtils.validateFileUploaderRules(fileUploaderApi);
  }

  @OnChange({ target: PseudonymSuggestionCheckAvailableAliases.FILE_UPLOADER })
  handleOnChangeFileUpload(event: ChangeEvent<Array<BasicFile>>): void {
    const files = [...event.value];
    this.fileUploderApi.setValue(files);
  }

  @OnClick({ target: PseudonymSuggestionCheckAvailableAliases.BUTTON_CONFIRM })
  handleOnClickButtonConfirm(event: ClickEvent): void {
    if (event) {
      this.formRequestApi.submit();
    }
  }

  @OnBeforeSubmit({ target: PseudonymSuggestionCheckAvailableAliases.FORM_REQUEST })
  handleOnBeforeSubmitFormRequest(event: AbortableSubmitEvent): void {
    const data = event.record as SuggestionPeriodForm;
    const dataReturn = {
      attachedPrescheduleFiles: data.uploadFiles,
      observation: data.observation,
      period: data.period.length ? this.getPeriodChard(data.period[0]) : ''
    };

    this.submittableFragmentContext.submit(new SubmitEvent({ record: dataReturn }));
  }

  private getPeriodChard(period: SuggestionPeriodItem): string {
    switch (period.id) {
      case 1: return SchedulePeriodType.MORNING;
      case 2: return SchedulePeriodType.EVENING;
      default: return SchedulePeriodType.NIGHT;
    }
  }

}
